import { Editor } from '@tiptap/core';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import StarterKit from '@tiptap/starter-kit';
import { csrfHeader } from '../../shared/csrf';
import { createOsmLayer, loadLeaflet } from './board/JobMap';

const ENHANCED_ATTRIBUTE = 'data-fj-company-job-editor-enhanced';

type JobEditorRoot = HTMLElement & {
    ownerDocument: Document;
};

function readDatasetUrl(element: HTMLElement, key: string): string | null {
    const value = element.dataset[key]?.trim() ?? '';
    return value.length > 0 ? value : null;
}

type AncillaryPayload = Record<string, unknown>;

type AncillarySuccessHandler = (payload: AncillaryPayload) => void;

function isRecord(value: unknown): value is AncillaryPayload {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function formCompanyId(form: HTMLFormElement): string {
    return form.querySelector<HTMLInputElement | HTMLSelectElement>('[name="companyId"]')?.value.trim() ?? '';
}

function encodedMainFormValues(form: HTMLFormElement, values: Record<string, string>): URLSearchParams {
    const body = new URLSearchParams();
    new FormData(form).forEach((value, name) => {
        if (typeof value === 'string' && name !== '_csrf') body.append(name, value);
    });
    Object.entries(values).forEach(([name, value]) => {
        body.delete(name);
        body.append(name, value);
    });
    return body;
}

function showInlineFeedback(element: HTMLElement | null, message: string, tone: 'error' | 'success'): void {
    if (!element) return;
    element.hidden = !message;
    element.textContent = message;
    element.dataset.fjFeedbackTone = tone;
}

async function submitAncillaryAction(
    page: JobEditorRoot,
    form: HTMLFormElement,
    button: HTMLButtonElement,
    actionUrl: string,
    values: Record<string, string>,
    feedback: HTMLElement | null,
    onSuccess: AncillarySuccessHandler
): Promise<void> {
    if (page.dataset.fjAncillaryBusy === 'true' || button.disabled) return;
    page.dataset.fjAncillaryBusy = 'true';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    showInlineFeedback(feedback, '', 'error');

    try {
        const response = await fetch(actionUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                ...csrfHeader(form),
                Accept: 'application/json',
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            },
            body: encodedMainFormValues(form, values),
        });
        let payload: unknown = null;
        try {
            payload = await response.json();
        } catch {
            payload = null;
        }
        if (!response.ok || !isRecord(payload)) throw new Error('Ancillary action failed.');
        onSuccess(payload);
        showInlineFeedback(feedback, 'Saved.', 'success');
    } catch {
        showInlineFeedback(feedback, 'Unable to save this change. Review the field and try again.', 'error');
    } finally {
        page.dataset.fjAncillaryBusy = 'false';
        button.disabled = false;
        button.removeAttribute('aria-busy');
    }
}

function initializeDescriptionEditor(page: JobEditorRoot, form: HTMLFormElement): Editor | null {
    const textarea = page.querySelector<HTMLTextAreaElement>('[data-fj-job-description-input]');
    const mount = page.querySelector<HTMLElement>('[data-fj-job-description-mount]');
    const editorContainer = page.querySelector<HTMLElement>('[data-fj-job-description-editor]');
    const status = page.querySelector<HTMLElement>('[data-fj-description-status]');
    if (!textarea || !mount || !editorContainer) return null;

    let editor: Editor;
    try {
        editor = new Editor({
            element: mount,
            extensions: [
                StarterKit.configure({ link: false }),
                Link.configure({
                    autolink: true,
                    openOnClick: false,
                }),
                Placeholder.configure({
                    placeholder: 'Describe the role, daily work, skills, pay and benefits, location, and how to apply.',
                }),
            ],
            content: textarea.value,
            editorProps: {
                attributes: {
                    class: 'fj-company-job-editor-prosemirror-content',
                    spellcheck: 'true',
                },
            },
            onUpdate: ({ editor: updatedEditor }) => {
                textarea.value = updatedEditor.getHTML();
            },
        });
    } catch {
        if (status) status.textContent = 'Plain text editor';
        return null;
    }

    editorContainer.hidden = false;
    textarea.hidden = true;
    if (status) status.textContent = 'Rich text editor enabled';

    page.querySelectorAll<HTMLButtonElement>('[data-fj-editor-command]').forEach((button) => {
        button.addEventListener('mousedown', (event) => event.preventDefault());
        button.addEventListener('click', () => {
            const command = button.dataset.fjEditorCommand;
            if (command === 'bold') editor.chain().focus().toggleBold().run();
            if (command === 'italic') editor.chain().focus().toggleItalic().run();
            if (command === 'bulletList') editor.chain().focus().toggleBulletList().run();
            if (command === 'orderedList') editor.chain().focus().toggleOrderedList().run();
            if (command === 'undo') editor.chain().focus().undo().run();
            if (command === 'redo') editor.chain().focus().redo().run();
            if (command === 'link') {
                const href = page.ownerDocument.defaultView?.prompt('Link URL', editor.getAttributes('link').href ?? '')?.trim() ?? '';
                if (!href) {
                    editor.chain().focus().unsetLink().run();
                    return;
                }
                editor.chain().focus().setLink({ href }).run();
            }
        });
    });

    form.addEventListener('submit', () => {
        textarea.value = editor.getHTML();
    });
    return editor;
}

function initializeApplicationDestination(page: JobEditorRoot): void {
    const urlInput = page.querySelector<HTMLInputElement>('[data-fj-application-url]');
    const externalFields = page.querySelector<HTMLElement>('[data-fj-external-application-fields]');
    const questionEditor = page.querySelector<HTMLElement>('[data-fj-question-editor]');
    const summary = page.querySelector<HTMLElement>('[data-fj-apply-summary]');
    const modes = Array.from(page.querySelectorAll<HTMLInputElement>('[data-fj-application-mode]'));
    if (modes.length === 0) return;

    const internalMode = modes.find((mode) => mode.dataset.fjApplicationMode === 'internal') ?? null;
    const externalMode = modes.find((mode) => mode.dataset.fjApplicationMode === 'external') ?? null;
    if (!modes.some((mode) => mode.checked) && internalMode) internalMode.checked = true;

    const sync = (): void => {
        const isExternal = externalMode?.checked === true;
        if (externalFields) externalFields.hidden = !isExternal;
        if (urlInput) urlInput.disabled = !isExternal;
        if (questionEditor) {
            questionEditor.hidden = isExternal;
            questionEditor.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | HTMLButtonElement>('input, textarea, select, button').forEach((control) => {
                control.disabled = isExternal;
            });
        }
        if (summary) summary.textContent = isExternal ? 'Employer website or ATS' : 'This career center';
    };

    modes.forEach((mode) => mode.addEventListener('change', sync));
    sync();
}

function initializePersistedSelections(page: JobEditorRoot): void {
    page.querySelectorAll<HTMLSelectElement>('[data-fj-current-value]').forEach((select) => {
        const value = select.dataset.fjCurrentValue?.trim() ?? '';
        if (!value) return;
        const option = Array.from(select.options).find((candidate) => candidate.value === value);
        if (option) select.value = value;
    });
}

function initializeBenefits(page: JobEditorRoot, form: HTMLFormElement): void {
    const options = page.querySelector<HTMLElement>('[data-fj-benefit-options]');
    const selectedSummary = page.querySelector<HTMLElement>('[data-fj-selected-benefits]');
    const countSummary = page.querySelector<HTMLElement>('[data-fj-benefit-count]');
    if (!options || !selectedSummary || !countSummary) return;

    const existing = new Set(
        (options.dataset.fjExistingBenefits ?? '')
            .split('|')
            .map((value) => value.trim())
            .filter(Boolean)
    );
    const checkboxes = (): HTMLInputElement[] => Array.from(
        options.querySelectorAll<HTMLInputElement>('input[name="benefits"]')
    );

    const render = (): void => {
        const selected = checkboxes().filter((checkbox) => checkbox.checked);
        countSummary.textContent = `${selected.length} selected`;
        selectedSummary.replaceChildren();
        selected.forEach((checkbox) => {
            const chip = page.ownerDocument.createElement('span');
            chip.className = 'fj-company-job-editor-chip';
            chip.textContent = checkbox.value;
            const remove = page.ownerDocument.createElement('button');
            remove.type = 'button';
            remove.className = 'fj-company-job-editor-chip-remove';
            remove.setAttribute('aria-label', `Remove ${checkbox.value}`);
            remove.textContent = '×';
            remove.addEventListener('click', () => {
                checkbox.checked = false;
                render();
            });
            chip.appendChild(remove);
            selectedSummary.appendChild(chip);
        });
    };

    const attachBenefitCheckbox = (checkbox: HTMLInputElement): void => {
        checkbox.addEventListener('change', render);
    };

    const appendBenefitOption = (benefit: AncillaryPayload): void => {
        const key = typeof benefit.key === 'string' ? benefit.key.trim() : '';
        const label = typeof benefit.label === 'string' ? benefit.label.trim() : '';
        if (!key || !label) throw new Error('Benefit response is incomplete.');
        const existingCheckbox = checkboxes().find((checkbox) => checkbox.value === label);
        if (existingCheckbox) {
            existingCheckbox.checked = true;
            render();
            return;
        }

        const option = page.ownerDocument.createElement('label');
        option.className = 'fj-company-job-editor-check fj-company-job-editor-benefit-option';
        option.dataset.fjBenefitOption = 'true';
        option.dataset.fjBenefitKey = key;
        option.dataset.fjBenefitLabel = label;
        const checkbox = page.ownerDocument.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.name = 'benefits';
        checkbox.value = label;
        checkbox.checked = true;
        const text = page.ownerDocument.createElement('span');
        text.textContent = label;
        option.append(checkbox, text);
        options.appendChild(option);
        attachBenefitCheckbox(checkbox);
        render();
    };

    existing.forEach((label) => {
        if (checkboxes().some((checkbox) => checkbox.value === label)) return;
        appendBenefitOption({ key: label, label });
    });
    checkboxes().forEach(attachBenefitCheckbox);
    render();

    const createButton = page.querySelector<HTMLButtonElement>('[data-fj-benefit-create]');
    const createFields = page.querySelector<HTMLElement>('[data-fj-benefit-create-fields]');
    const createSubmit = page.querySelector<HTMLButtonElement>('[data-fj-benefit-create-submit]');
    const newLabel = page.querySelector<HTMLInputElement>('[data-fj-new-benefit-label]');
    const feedback = page.querySelector<HTMLElement>('[data-fj-benefit-feedback]');
    const createUrl = readDatasetUrl(page, 'fjBenefitCreateUrl');
    createButton?.addEventListener('click', () => {
        if (!createFields) return;
        createFields.hidden = !createFields.hidden;
        if (!createFields.hidden) newLabel?.focus();
    });
    createSubmit?.addEventListener('click', () => {
        const label = newLabel?.value.trim() ?? '';
        const companyId = formCompanyId(form);
        if (!createUrl || !label || !companyId) {
            showInlineFeedback(feedback, 'Enter a benefit and confirm the employer account.', 'error');
            newLabel?.focus();
            return;
        }
        void submitAncillaryAction(
            page,
            form,
            createSubmit,
            createUrl,
            { benefitLabel: label, companyId },
            feedback,
            (payload) => {
                const benefit = isRecord(payload.benefit) ? payload.benefit : null;
                if (!benefit) throw new Error('Benefit response is incomplete.');
                appendBenefitOption(benefit);
                if (newLabel) newLabel.value = '';
                if (createFields) createFields.hidden = true;
            }
        );
    });
}

function initializeQuestionLibrary(page: JobEditorRoot, form: HTMLFormElement): void {
    const library = page.querySelector<HTMLElement>('[data-fj-approved-question-library]');
    const search = page.querySelector<HTMLInputElement>('[data-fj-question-search]');
    const approvedQuestions = Array.from(
        library?.querySelectorAll<HTMLElement>('[data-fj-question]') ?? []
    );
    const existingKeys = new Set(
        (library?.dataset.fjExistingQuestionKeys ?? '')
            .split('|')
            .map((value) => value.trim())
            .filter(Boolean)
    );
    library?.querySelectorAll<HTMLInputElement>('input[name="requiredFieldTemplateKeys"]').forEach((input) => {
        if (existingKeys.has(input.value)) input.checked = true;
    });

    const proposalUrl = readDatasetUrl(page, 'fjQuestionProposalUrl');
    const proposalButton = page.querySelector<HTMLButtonElement>('[data-fj-question-propose]');
    const prompt = page.querySelector<HTMLInputElement>('[data-fj-question-proposal-prompt]');
    const type = page.querySelector<HTMLSelectElement>('[data-fj-question-proposal-type]');
    const required = page.querySelector<HTMLInputElement>('[data-fj-question-proposal-required]');
    const choicesFields = page.querySelector<HTMLElement>('[data-fj-question-proposal-choices-fields]');
    const choices = page.querySelector<HTMLTextAreaElement>('[data-fj-question-proposal-choices]');
    const feedback = page.querySelector<HTMLElement>('[data-fj-question-feedback]');
    const syncChoices = (): void => {
        const visible = type?.value === 'single_choice';
        if (choicesFields) choicesFields.hidden = !visible;
        if (choices) choices.disabled = !visible;
    };
    const syncQuestionSearch = (): void => {
        const query = search?.value.trim().toLowerCase() ?? '';
        let matchCount = 0;
        approvedQuestions.forEach((question) => {
            const text = (question.dataset.fjQuestionSearchText ?? question.textContent ?? '').toLowerCase();
            const matches = !query || text.includes(query);
            question.hidden = !matches;
            if (matches && query) matchCount += 1;
        });
        if (proposalButton) proposalButton.hidden = !proposalUrl || !query || matchCount > 0;
    };
    const appendPendingQuestion = (question: AncillaryPayload): void => {
        const name = typeof question.name === 'string' ? question.name.trim() : '';
        const label = typeof question.label === 'string' ? question.label.trim() : '';
        const typeValue = typeof question.type === 'string' ? question.type.trim() : '';
        const status = typeof question.status === 'string' ? question.status.trim() : '';
        if (!name || !label || !typeValue || status !== 'pending' || typeof question.required !== 'boolean') {
            throw new Error('Question response is incomplete.');
        }
        const group = page.querySelector<HTMLElement>('[data-fj-pending-questions-group]');
        const list = page.querySelector<HTMLElement>('[data-fj-pending-question-list]');
        if (!group || !list) throw new Error('Pending question list is unavailable.');

        const row = page.ownerDocument.createElement('div');
        row.className = 'fj-company-job-editor-question fj-company-job-editor-question-pending';
        row.dataset.fjQuestion = 'true';
        row.dataset.fjQuestionName = name;
        row.dataset.fjQuestionSearchText = `${label} ${typeValue}`;
        const content = page.ownerDocument.createElement('span');
        const title = page.ownerDocument.createElement('strong');
        title.textContent = label;
        const details = page.ownerDocument.createElement('small');
        details.textContent = `Pending review · ${typeValue}${question.required ? ' · Required' : ''}`;
        content.append(title, details);
        row.appendChild(content);
        list.appendChild(row);
        group.hidden = false;
    };

    type?.addEventListener('change', syncChoices);
    search?.addEventListener('input', syncQuestionSearch);
    // Searching/proposing a question must never submit the enclosing job form.
    search?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') event.preventDefault();
    });
    proposalButton?.addEventListener('click', () => {
        const value = prompt?.value.trim() ?? '';
        const companyId = formCompanyId(form);
        if (!proposalUrl || !value || !companyId) {
            showInlineFeedback(feedback, 'Write a question and confirm the employer account.', 'error');
            prompt?.focus();
            return;
        }
        const selectedType = type?.value ?? 'short_text';
        const choiceValue = choices?.value.trim() ?? '';
        if (selectedType === 'single_choice' && !choiceValue) {
            showInlineFeedback(feedback, 'Add at least one choice for this question.', 'error');
            choices?.focus();
            return;
        }
        void submitAncillaryAction(
            page,
            form,
            proposalButton,
            proposalUrl,
            {
                prompt: value,
                type: selectedType,
                required: required?.checked ? 'true' : 'false',
                choices: choiceValue,
                companyId,
            },
            feedback,
            (payload) => {
                const question = isRecord(payload.question) ? payload.question : null;
                if (!question) throw new Error('Question response is incomplete.');
                appendPendingQuestion(question);
                if (prompt) prompt.value = '';
                syncQuestionSearch();
            }
        );
    });
    syncChoices();
    syncQuestionSearch();
}

function initializeWorkflowActions(page: JobEditorRoot, form: HTMLFormElement): void {
    const publishForm = page.querySelector<HTMLFormElement>('[data-fj-publish-form]');
    const publishButton = page.querySelector<HTMLButtonElement>('[data-fj-publish-action]');
    const warning = page.querySelector<HTMLElement>('[data-fj-unsaved-warning]');
    if (!publishForm || !publishButton) return;

    let dirty = false;
    const setDirty = (value: boolean): void => {
        dirty = value;
        publishButton.disabled = dirty || page.dataset.fjJobStatus !== 'draft';
        if (warning) warning.hidden = !dirty;
    };
    form.addEventListener('input', () => setDirty(true));
    form.addEventListener('change', () => setDirty(true));
    publishForm.addEventListener('submit', (event) => {
        if (!dirty && page.dataset.fjJobStatus === 'draft') return;
        event.preventDefault();
        publishButton.focus();
        if (warning) warning.hidden = false;
    });
    setDirty(false);
}

function initializeCompanySelection(page: JobEditorRoot, form: HTMLFormElement): void {
    if (page.dataset.fjJobMode !== 'add') return;
    const select = form.querySelector<HTMLSelectElement>('select[name="companyId"]');
    const view = page.ownerDocument.defaultView;
    if (!select || !view) return;

    let currentCompanyId = select.value.trim();
    select.addEventListener('change', () => {
        const nextCompanyId = select.value.trim();
        if (!nextCompanyId || nextCompanyId === currentCompanyId) return;
        const continueSwitch = typeof view.confirm !== 'function'
            || view.confirm('Switching employers reloads this editor and discards unsaved changes. Continue?');
        if (!continueSwitch) {
            select.value = currentCompanyId;
            return;
        }
        currentCompanyId = nextCompanyId;
        const url = new URL(view.location.href);
        url.searchParams.set('companyId', nextCompanyId);
        view.location.assign(`${url.pathname}${url.search}${url.hash}`);
    });
}

function initializePostingLocation(page: JobEditorRoot): void {
    const root = page.querySelector<HTMLElement>('[data-fj-posting-location]');
    const address = root?.querySelector<HTMLInputElement>('[data-fj-posting-address]');
    const latitude = root?.querySelector<HTMLInputElement>('[data-fj-posting-latitude]');
    const longitude = root?.querySelector<HTMLInputElement>('[data-fj-posting-longitude]');
    const precision = root?.querySelector<HTMLInputElement>('[data-fj-posting-precision]');
    const source = root?.querySelector<HTMLInputElement>('[data-fj-posting-source]');
    const confirmed = root?.querySelector<HTMLInputElement>('[data-fj-posting-confirmed]');
    const toggle = root?.querySelector<HTMLButtonElement>('[data-fj-posting-map-toggle]');
    const status = root?.querySelector<HTMLElement>('[data-fj-posting-map-status]');
    const canvas = root?.querySelector<HTMLElement>('[data-fj-posting-map]');
    const confirmCenter = root?.querySelector<HTMLButtonElement>('[data-fj-posting-map-confirm]');
    if (!root || !address || !latitude || !longitude || !precision || !source || !confirmed || !toggle || !status || !canvas || !confirmCenter) return;

    const originalAddress = address.dataset.originalAddress?.trim() ?? address.value.trim();
    address.addEventListener('input', () => {
        if (address.value.trim() === originalAddress) return;
        latitude.value = '';
        longitude.value = '';
        precision.value = 'unmapped';
        source.value = 'employer-entered';
        confirmed.value = 'false';
        status.textContent = 'Address changed. The old exact pin was cleared.';
    });

    toggle.addEventListener('click', async () => {
        if (!canvas.hidden) {
            canvas.hidden = true;
            toggle.setAttribute('aria-expanded', 'false');
            toggle.textContent = 'Place pin on map';
            confirmCenter.hidden = true;
            confirmCenter.disabled = true;
            return;
        }
        canvas.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
        toggle.textContent = 'Hide pin map';
        if (canvas.dataset.fjPostingMapReady === 'true') {
            confirmCenter.hidden = false;
            confirmCenter.disabled = false;
            canvas.focus();
            return;
        }
        status.textContent = 'Loading the map…';
        try {
            const L = await loadLeaflet();
            const currentLat = Number(latitude.value);
            const currentLng = Number(longitude.value);
            const hasPin = Number.isFinite(currentLat) && Number.isFinite(currentLng)
                && currentLat >= -90 && currentLat <= 90 && currentLng >= -180 && currentLng <= 180
                && latitude.value !== '' && longitude.value !== '';
            const map = L.map(canvas, { keyboard: true }).setView(hasPin ? [currentLat, currentLng] : [39.5, -98.35], hasPin ? 12 : 4);
            createOsmLayer(L).addTo(map);
            let marker = hasPin ? L.marker([currentLat, currentLng]).addTo(map) : null;
            const confirmPin = (point: { lat: number; lng: number }): void => {
                const lat = Number(point.lat.toFixed(6));
                const lng = Number(point.lng.toFixed(6));
                latitude.value = String(lat);
                longitude.value = String(lng);
                precision.value = 'exact';
                source.value = 'employer-confirmed';
                confirmed.value = 'true';
                if (marker) marker.setLatLng([lat, lng]);
                else marker = L.marker([lat, lng]).addTo(map);
                status.textContent = 'Exact pin confirmed. Save changes to use it publicly.';
                latitude.dispatchEvent(new Event('change', { bubbles: true }));
            };
            map.on('click', (event: { latlng: { lat: number; lng: number } }) => confirmPin(event.latlng));
            confirmCenter.addEventListener('click', () => confirmPin(map.getCenter()));
            canvas.dataset.fjPostingMapReady = 'true';
            confirmCenter.hidden = false;
            confirmCenter.disabled = false;
            status.textContent = `${hasPin ? 'Current pin shown.' : 'No exact pin selected.'} Use arrow keys to move the map, then choose Use map center as exact pin. You can also click the map.`;
            canvas.focus();
        } catch {
            canvas.hidden = true;
            toggle.setAttribute('aria-expanded', 'false');
            toggle.textContent = 'Try pin map again';
            confirmCenter.hidden = true;
            confirmCenter.disabled = true;
            status.textContent = 'The map could not load. You can still save the address without an exact pin.';
        }
    });
}

export function initializeCompanyJobEditor(root: ParentNode = document): void {
    root.querySelectorAll<HTMLElement>('[data-fj-company-job-editor]').forEach((page) => {
        if (page.hasAttribute(ENHANCED_ATTRIBUTE)) return;
        page.setAttribute(ENHANCED_ATTRIBUTE, 'true');
        const form = page.querySelector<HTMLFormElement>('[data-fj-job-form]');
        if (!form) return;

        const editor = initializeDescriptionEditor(page as JobEditorRoot, form);
        initializePersistedSelections(page as JobEditorRoot);
        initializeApplicationDestination(page as JobEditorRoot);
        initializeBenefits(page as JobEditorRoot, form);
        initializeQuestionLibrary(page as JobEditorRoot, form);
        initializeWorkflowActions(page as JobEditorRoot, form);
        initializeCompanySelection(page as JobEditorRoot, form);
        initializePostingLocation(page as JobEditorRoot);

        if (editor) {
            form.addEventListener('reset', () => editor.commands.clearContent());
        }
    });
}
